import { useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import { useStockingCyclesController_create } from '@/services/hooks';

import { invalidateSiteBoardQueries } from './_invalidateSiteBoard';
import { mapCreateStockingCycleToRequest, readCreatedCycleId } from './mappers';
import type { CreateStockingCycleInput } from './types';

export interface UseCreateStockingCycleResult {
    /** Resolves to the new cycle's id once the board has refetched; rejects on a request error. */
    create: (input: CreateStockingCycleInput) => Promise<number | null>;
    isLoading: boolean;
    error: Error | RequestError | null;
    reset: () => void;
}

/** Creates a cycle at the site. */
export const useCreateStockingCycle = (
    clientId: number,
    locationId: number
): UseCreateStockingCycleResult => {
    const queryClient = useQueryClient();
    const mutation = useStockingCyclesController_create();

    const create = async (input: CreateStockingCycleInput): Promise<number | null> => {
        const response = await mutation.mutateAsync({
            clientId,
            locationId,
            requestBody: mapCreateStockingCycleToRequest(input),
        });
        await invalidateSiteBoardQueries(queryClient, clientId, locationId);
        return readCreatedCycleId(response.data);
    };

    return {
        create,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
